/**
 * visitor-token — the single WebVisitor token coordinator (CDP contract C1).
 *
 * TWO token-less paths can run on a first visit: the visitor-events bootstrap
 * (fired un-awaited from init — visitor-events.ts) and the conversation
 * open/resume (fired from the widget's lazy start — wa-resume.ts). Each used to
 * read storage on its own, so a slow bootstrap still in flight when the visitor
 * opened the widget minted TWO WebVisitors — and with localStorage blocked the
 * split was DETERMINISTIC (events kept an in-memory fallback, wa-resume did
 * not, so every open re-minted). This module is the single source of truth both
 * paths share:
 *
 *   stored token → shared in-memory token → JOIN the single in-flight
 *   acquisition (whichever path starts first — an events bootstrap OR an
 *   open/resume — becomes the acquirer; the other awaits it and reuses).
 *
 * Both paths write back through here, so one page load = one identity even
 * when localStorage is unavailable (private mode / SSR).
 */

/** localStorage key for the server-minted WebVisitor token. Why it exists, why
 *  it is server-issued, and why localStorage: see the doc on wa-resume.ts's
 *  re-export — kept there with the rest of the FLO-544 continuity story. */
export const WA_VISITOR_KEY = 'floe_wa_visitor';

/** In-memory fallback so the token is stable within a page load when
 *  localStorage is unavailable (private mode / SSR) — mirrors anonymous-id.ts. */
let fallbackToken: string | null = null;

/**
 * THE single in-flight token acquisition (an events bootstrap/flush mint OR a
 * conversation open). Every other token-less path joins it instead of firing
 * its own token-minting request: concurrent token-less requests each make the
 * server mint a fresh WebVisitor, and last-write-wins in storage would split
 * the person's events and conversations across identities. Null when nothing
 * is acquiring.
 */
let acquisitionInFlight: Promise<void> | null = null;

/** The visitor token, or null when this visitor has none yet. Never throws. */
export function getStoredVisitorToken(): string | null {
  try {
    const stored = localStorage.getItem(WA_VISITOR_KEY);
    if (stored && stored.trim()) return stored;
  } catch {
    /* localStorage blocked */
  }
  return fallbackToken;
}

/** Persist a server-minted token. Junk/empty is ignored rather than stored. */
export function storeVisitorToken(token: unknown): void {
  if (typeof token !== 'string' || !token.trim()) return;
  fallbackToken = token;
  try {
    localStorage.setItem(WA_VISITOR_KEY, token);
  } catch {
    /* quota / blocked — the in-memory fallback still covers this page load */
  }
}

/** Forget the visitor identity (teardown / hard reset). Never throws. */
export function clearVisitorToken(): void {
  fallbackToken = null;
  try {
    localStorage.removeItem(WA_VISITOR_KEY);
  } catch {
    /* blocked — the memory copy is gone, which is what matters in-page */
  }
}

/** The in-flight acquisition to JOIN, or null when this path should acquire. */
export function pendingTokenAcquisition(): Promise<void> | null {
  return acquisitionInFlight;
}

/**
 * Become THE acquirer. Returns the settle function the acquirer MUST call once
 * its request has settled — success or failure alike (i.e. in a `finally`),
 * AFTER any {@link storeVisitorToken} write — which releases the latch (only
 * if still current) and wakes every joined path to re-read
 * {@link getStoredVisitorToken} and decide: token → use it; none → the first
 * to notice becomes the replacement acquirer.
 */
export function beginTokenAcquisition(): () => void {
  let settle!: () => void;
  const pending = new Promise<void>((resolve) => {
    settle = resolve;
  });
  acquisitionInFlight = pending;
  return () => {
    if (acquisitionInFlight === pending) acquisitionInFlight = null;
    settle();
  };
}
